/**
 * DecisionMapView
 *
 * The "seeing surface" for the decision log: decisions rendered as a forest
 * (parent → sub-decision), with supersession/override edges, subject-kind
 * filters, and Journal / Register / Docket view modes. Selecting a node fills
 * the dossier panel — who, when, where, why, effects, standing links, and
 * revisit conditions. Selection is URL-addressable via ?view=map&selected=<id>
 * so a decision's answer is literally a URL.
 *
 * Canon: docs/1-canon/27-decision-ontology.md (Proposed), 17-decision-recording-spec.md
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Pencil, ExternalLink } from 'lucide-react';
import { decisionPagePath } from './decisionRecordText';
import type { Decision, DecisionSubjectKind } from '../../../types/prioritization/decisions';
import {
  DECISION_SUBJECT_KIND_LABELS,
  DECISION_SUBJECT_KIND_HINTS,
  DECISION_SUBJECT_KIND_COLORS,
  resolveSubjectKind,
} from '../../../types/prioritization/decisions';

// ============================================================================
// Types
// ============================================================================

interface ResolvedRef { kind: string; id: string; name: string; href: string }

interface DecisionMapViewProps {
  decisions: Decision[];
  actorDisplayMap: Record<string, string>;
  resolvedObjects: Record<string, ResolvedRef[]>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onEdit?: (d: Decision) => void;
}

type MapViewMode = 'journal' | 'register' | 'docket';

const UNCLASSIFIED_COLOR = '#9CA3AF';

// Node geometry
const NW = 236;
const NH = 62;
const XGAP = 72;
const YGAP = 16;
const MARGIN = 24;
const TREE_GAP = 0.45; // extra leaf-units between root trees

// ============================================================================
// Helpers
// ============================================================================

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
}

function isReviewDue(d: Decision): boolean {
  return d.status === 'active' && !!d.reviewByDate && new Date(d.reviewByDate).getTime() < Date.now();
}

function wrapTwoLines(s: string, max: number): string[] {
  if (s.length <= max) return [s];
  let cut = s.lastIndexOf(' ', max);
  if (cut < 12) cut = max;
  let rest = s.slice(cut + 1);
  if (rest.length > max + 4) rest = `${rest.slice(0, max + 1).replace(/\s+\S*$/, '')}…`;
  return [s.slice(0, cut), rest];
}

interface LayoutResult {
  pos: Record<string, { x: number; y: number }>;
  width: number;
  height: number;
  childrenMap: Record<string, string[]>;
  roots: string[];
}

/** Forest layout: horizontal trees, roots left, leaves spread vertically. */
function layoutForest(decisions: Decision[]): LayoutResult {
  const byId = new Map(decisions.map((d) => [d.id, d]));
  const childrenMap: Record<string, string[]> = {};
  const roots: string[] = [];

  for (const d of decisions) {
    if (d.parentDecisionId && byId.has(d.parentDecisionId) && d.parentDecisionId !== d.id) {
      (childrenMap[d.parentDecisionId] ??= []).push(d.id);
    } else {
      roots.push(d.id);
    }
  }
  const byDate = (a: string, b: string) =>
    (byId.get(a)?.decidedAt ?? '').localeCompare(byId.get(b)?.decidedAt ?? '');
  roots.sort(byDate);
  Object.values(childrenMap).forEach((kids) => kids.sort(byDate));

  const pos: Record<string, { x: number; y: number }> = {};
  const visited = new Set<string>();
  let leafCursor = 0;
  let maxDepth = 0;

  const layout = (id: string, depth: number): number => {
    // Cycle guard: a malformed parent chain must not hang the renderer.
    if (visited.has(id)) return leafCursor;
    visited.add(id);
    maxDepth = Math.max(maxDepth, depth);
    const kids = (childrenMap[id] ?? []).filter((k) => !visited.has(k));
    let yUnit: number;
    if (kids.length === 0) {
      yUnit = leafCursor;
      leafCursor += 1;
    } else {
      const ys = kids.map((k) => layout(k, depth + 1));
      yUnit = (Math.min(...ys) + Math.max(...ys)) / 2;
    }
    pos[id] = { x: MARGIN + depth * (NW + XGAP), y: MARGIN + yUnit * (NH + YGAP) };
    return yUnit;
  };

  roots.forEach((r, i) => {
    if (i > 0) leafCursor += TREE_GAP;
    layout(r, 0);
  });

  return {
    pos,
    width: MARGIN * 2 + (maxDepth + 1) * (NW + XGAP) - XGAP,
    height: MARGIN * 2 + Math.max(1, leafCursor) * (NH + YGAP) - YGAP,
    childrenMap,
    roots,
  };
}

// ============================================================================
// Component
// ============================================================================

export function DecisionMapView({
  decisions,
  actorDisplayMap,
  resolvedObjects,
  selectedId,
  onSelect,
  onEdit,
}: DecisionMapViewProps) {
  const [mode, setMode] = useState<MapViewMode>('journal');
  const [kindsOff, setKindsOff] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  const byId = useMemo(() => new Map(decisions.map((d) => [d.id, d])), [decisions]);
  const { pos, width, height, childrenMap } = useMemo(() => layoutForest(decisions), [decisions]);

  const kindOf = (d: Decision): DecisionSubjectKind | 'unclassified' => resolveSubjectKind(d) ?? 'unclassified';

  const supersededBy = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const d of decisions) {
      if (d.supersedesDecisionId && byId.has(d.supersedesDecisionId)) {
        (map[d.supersedesDecisionId] ??= []).push(d.id);
      }
    }
    return map;
  }, [decisions, byId]);

  const kindCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of decisions) counts[kindOf(d)] = (counts[kindOf(d)] ?? 0) + 1;
    return counts;
  }, [decisions]);

  const visible = (d: Decision): boolean => {
    if (kindsOff.has(kindOf(d))) return false;
    if (mode === 'register') return d.status === 'active';
    if (mode === 'docket') return isReviewDue(d);
    return true;
  };

  const selected = selectedId ? byId.get(selectedId) ?? null : null;
  const ancestry = useMemo(() => {
    const chain = new Set<string>();
    let cur = selected;
    while (cur?.parentDecisionId && byId.has(cur.parentDecisionId) && !chain.has(cur.parentDecisionId)) {
      chain.add(cur.parentDecisionId);
      cur = byId.get(cur.parentDecisionId) ?? null;
    }
    return chain;
  }, [selected, byId]);

  const toggleKind = (k: string) => {
    setKindsOff((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const copyLink = async () => {
    if (!selected) return;
    const url = `${window.location.origin}/prioritization/decisions?view=map&selected=${selected.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the visible path still communicates the URL */
    }
  };

  const kindEntries: { key: DecisionSubjectKind | 'unclassified'; label: string; hint: string; color: string }[] = [
    ...(Object.keys(DECISION_SUBJECT_KIND_LABELS) as DecisionSubjectKind[]).map((k) => ({
      key: k,
      label: DECISION_SUBJECT_KIND_LABELS[k],
      hint: DECISION_SUBJECT_KIND_HINTS[k],
      color: DECISION_SUBJECT_KIND_COLORS[k],
    })),
    { key: 'unclassified' as const, label: 'Unclassified', hint: 'No subject kind yet', color: UNCLASSIFIED_COLOR },
  ];

  const modeDefs: { key: MapViewMode; label: string; hint: string }[] = [
    { key: 'journal',  label: 'Journal',  hint: 'Everything recorded' },
    { key: 'register', label: 'Register', hint: 'What stands right now' },
    { key: 'docket',   label: 'Docket',   hint: 'Reviews due' },
  ];

  if (decisions.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-10 text-center text-sm text-[var(--text-ter)]">
        No decisions recorded yet. The map draws itself as decisions are memorialized.
      </div>
    );
  }

  const anyVisible = decisions.some(visible);

  return (
    <div className="flex flex-col gap-3">
      {/* Controls */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-3 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {modeDefs.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              title={m.hint}
              className={`px-3 py-1.5  text-sm font-medium transition-colors ${
                mode === m.key ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              {m.label}
              <span className="ml-1.5 hidden sm:inline text-sm font-normal opacity-70">{m.hint}</span>
            </button>
          ))}
          <span className="ml-auto flex items-center gap-3 text-sm text-[var(--text-ter)]">
            <span className="inline-flex items-center gap-1.5">
              <svg width="22" height="8" aria-hidden="true"><path d="M1,7 Q11,-3 21,7" fill="none" stroke="#DC2626" strokeWidth="1.5" strokeDasharray="4 3" /></svg>
              supersedes
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg width="18" height="10" aria-hidden="true"><rect x="1" y="1" width="16" height="8" rx="2.5" fill="var(--surface)" stroke="#F59E0B" strokeDasharray="3 2" /></svg>
              review due
            </span>
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {kindEntries.filter((k) => (kindCounts[k.key] ?? 0) > 0).map((k) => {
            const off = kindsOff.has(k.key);
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => toggleKind(k.key)}
                aria-pressed={!off}
                title={k.hint}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1  text-sm font-medium border transition-colors ${
                  off ? 'bg-[var(--surface)] text-[var(--text-ter)] border-[var(--rule)]' : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:bg-[var(--surface)]'
                }`}
              >
                <span className="h-2 w-2" style={{ backgroundColor: off ? '#D1D5DB' : k.color }} />
                {k.label}
                <span className="text-[var(--text-ter)]">{kindCounts[k.key]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Map + dossier */}
      <div className="flex flex-col lg:flex-row gap-3 items-stretch">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] flex-1 overflow-auto relative" style={{ maxHeight: '70vh' }}>
          {!anyVisible && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-[var(--text-ter)] pointer-events-none">
              Nothing matches the current view — clear a filter or switch back to Journal.
            </div>
          )}
          <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="group" aria-label="Decision map">
            {/* tree edges */}
            {decisions.map((d) => {
              if (!d.parentDecisionId || !pos[d.parentDecisionId] || !pos[d.id]) return null;
              const p = pos[d.parentDecisionId];
              const c = pos[d.id];
              const x1 = p.x + NW, y1 = p.y + NH / 2, x2 = c.x, y2 = c.y + NH / 2;
              const mx = (x1 + x2) / 2;
              const on = visible(d) && visible(byId.get(d.parentDecisionId)!);
              const inTrail = selectedId != null && (ancestry.has(d.parentDecisionId) || d.parentDecisionId === selectedId || d.id === selectedId) && (ancestry.has(d.id) || d.id === selectedId);
              return (
                <path
                  key={`e-${d.id}`}
                  d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                  fill="none"
                  stroke={inTrail ? '#7C3AED' : 'var(--rule-bright)'}
                  strokeWidth={inTrail ? 2 : 1.4}
                  opacity={on ? 1 : 0.15}
                />
              );
            })}
            {/* supersession edges */}
            {decisions.map((d) => {
              if (!d.supersedesDecisionId || !pos[d.supersedesDecisionId] || !pos[d.id]) return null;
              const a = pos[d.id];
              const b = pos[d.supersedesDecisionId];
              const x1 = a.x + NW / 2, y1 = a.y + (b.y > a.y ? NH : 0);
              const x2 = b.x + NW / 2, y2 = b.y + (b.y > a.y ? 0 : NH);
              const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
              const on = visible(d) && visible(byId.get(d.supersedesDecisionId)!);
              return (
                <g key={`s-${d.id}`} opacity={on ? 0.9 : 0.12}>
                  <path d={`M${x1},${y1} Q${mx},${my} ${x2},${y2}`} fill="none" stroke="#DC2626" strokeWidth={1.5} strokeDasharray="5 4" />
                  <text x={mx} y={my - 6} textAnchor="middle" fontSize={12} letterSpacing={1.3} fill="#DC2626">
                    SUPERSEDES
                  </text>
                </g>
              );
            })}
            {/* nodes */}
            {decisions.map((d) => {
              const p = pos[d.id];
              if (!p) return null;
              const on = visible(d);
              const kind = kindOf(d);
              const color = kind === 'unclassified' ? UNCLASSIFIED_COLOR : DECISION_SUBJECT_KIND_COLORS[kind];
              const isSelected = d.id === selectedId;
              const dimmedStanding = d.status !== 'active';
              const reviewDue = isReviewDue(d);
              const lines = wrapTwoLines(d.title, 36);
              return (
                <g
                  key={d.id}
                  transform={`translate(${p.x},${p.y})`}
                  opacity={on ? (dimmedStanding ? 0.6 : 1) : 0.1}
                  className="cursor-pointer focus:outline-none"
                  tabIndex={on ? 0 : -1}
                  role="button"
                  aria-label={`${d.title} — ${kind}, ${d.status}${reviewDue ? ', review due' : ''}`}
                  onClick={() => onSelect(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(d.id);
                    }
                  }}
                >
                  <rect
                    width={NW}
                    height={NH}
                    rx={8}
                    fill={dimmedStanding ? 'var(--bg-raised)' : 'var(--surface)'}
                    stroke={isSelected ? '#7C3AED' : reviewDue ? '#F59E0B' : 'var(--rule)'}
                    strokeWidth={isSelected ? 2 : 1.2}
                    strokeDasharray={reviewDue && !isSelected ? '4 3' : undefined}
                  />
                  <rect x={0} y={8} width={3} height={NH - 16} rx={1.5} fill={color} />
                  <text x={14} y={17} fontSize={12} letterSpacing={1} fill="var(--text-ter)">
                    {formatDate(d.decidedAt).toUpperCase()} · {(kind === 'unclassified' ? 'UNCLASSIFIED' : DECISION_SUBJECT_KIND_LABELS[kind].toUpperCase())}
                  </text>
                  {lines.map((ln, i) => (
                    <text
                      key={i}
                      x={14}
                      y={34 + i * 14}
                      fontSize={13}
                      fill={dimmedStanding ? 'var(--text-ter)' : 'var(--text-pri)'}
                      textDecoration={d.status === 'superseded' || d.status === 'reversed' ? 'line-through' : undefined}
                    >
                      {ln}
                    </text>
                  ))}
                  {(d.status !== 'active' || reviewDue) && (
                    <text x={NW - 10} y={17} textAnchor="end" fontSize={12} letterSpacing={1}
                      fill={reviewDue ? '#B45309' : 'var(--text-dim)'}>
                      {d.status !== 'active' ? d.status.toUpperCase() : 'REVIEW DUE'}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Dossier */}
        <aside className="bg-[var(--bg-raised)] border border-[var(--rule)] w-full lg:w-96 lg:flex-none p-5 overflow-y-auto" style={{ maxHeight: '70vh' }} aria-live="polite">
          {!selected ? (
            <div className="text-center text-base text-[var(--text-ter)] mt-8 px-3">
              <p className="text-lg font-medium text-[var(--text-sec)] mb-2">Select a decision.</p>
              Every node answers <em>who, when, why</em> — and links to whatever superseded or reopened it. The selection is a shareable URL.
            </div>
          ) : (
            <DossierPanel
              decision={selected}
              byId={byId}
              childrenIds={childrenMap[selected.id] ?? []}
              supersededByIds={supersededBy[selected.id] ?? []}
              actorDisplayMap={actorDisplayMap}
              refs={resolvedObjects[selected.id] ?? []}
              onSelect={onSelect}
              onEdit={onEdit}
              onCopy={copyLink}
              copied={copied}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

// ============================================================================
// Dossier panel
// ============================================================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-medium uppercase tracking-wider text-[var(--text-ter)] border-b border-[var(--rule)] pb-1 mb-2">
      {children}
    </div>
  );
}

function DossierPanel({
  decision: d,
  byId,
  childrenIds,
  supersededByIds,
  actorDisplayMap,
  refs,
  onSelect,
  onEdit,
  onCopy,
  copied,
}: {
  decision: Decision;
  byId: Map<string, Decision>;
  childrenIds: string[];
  supersededByIds: string[];
  actorDisplayMap: Record<string, string>;
  refs: ResolvedRef[];
  onSelect: (id: string | null) => void;
  onEdit?: (d: Decision) => void;
  onCopy: () => void;
  copied: boolean;
}) {
  const kind = resolveSubjectKind(d) ?? 'unclassified';
  const kindColor = kind === 'unclassified' ? UNCLASSIFIED_COLOR : DECISION_SUBJECT_KIND_COLORS[kind];
  const kindLabel = kind === 'unclassified' ? 'Unclassified' : DECISION_SUBJECT_KIND_LABELS[kind];
  const who = d.decidedBy
    ? actorDisplayMap[d.decidedBy] ?? d.decidedByName ?? d.decidedBy.slice(0, 8)
    : d.decidedByName ?? (d.decisionKind === 'auto' ? 'System-recorded' : 'Unattributed');
  const reviewDue = isReviewDue(d);

  const linkButton = (id: string, rel: string, relColor: string) => {
    const target = byId.get(id);
    if (!target) return null;
    return (
      <button
        key={`${rel}-${id}`}
        type="button"
        onClick={() => onSelect(id)}
        className="block w-full text-left bg-[var(--surface)] hover:bg-[var(--hover-tint)] px-3 py-2 text-sm text-[var(--text-sec)] transition-colors"
      >
        <span className="block text-xs font-medium uppercase tracking-wider mb-0.5" style={{ color: relColor }}>
          {rel}
        </span>
        {target.title}
      </button>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 bg-[var(--surface)] px-3 py-2 mb-3">
        <span className="flex-1 text-sm text-[var(--text-ter)] break-all font-mono">
          /prioritization/decisions/{d.id.slice(0, 8)}…
        </span>
        <Link
          to={decisionPagePath(d.id)}
          title="Open this decision's page"
          className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          onClick={onCopy}
          title="Copy link to this decision"
          className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span
          className="inline-block px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wide"
          style={{ backgroundColor: `${kindColor}1A`, color: kindColor }}
        >
          {kindLabel}
        </span>
        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wide bg-[var(--hover-tint)] text-[var(--text-sec)]">
          {d.status}{reviewDue ? ' · review due' : ''}
        </span>
        {onEdit && d.decisionKind === 'manual' && d.status === 'active' && (
          <button
            type="button"
            onClick={() => onEdit(d)}
            title="Edit decision"
            className="ml-auto text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <h3 className="text-lg font-semibold text-[var(--text-pri)] leading-snug mb-4">{d.title}</h3>

      <div className="space-y-4">
        <div>
          <SectionLabel>Who · how</SectionLabel>
          <p className="text-sm text-[var(--text-pri)] font-medium">{who}</p>
          <p className="text-sm text-[var(--text-ter)] mt-0.5 capitalize">{d.decisionKind} decision{d.decisionClass ? ` · ${d.decisionClass.replace('_', ' ')}` : ''}</p>
        </div>

        <div>
          <SectionLabel>When · where</SectionLabel>
          <p className="text-sm text-[var(--text-sec)]">{formatDate(d.decidedAt)}</p>
          {d.site && <p className="text-sm text-[var(--text-ter)] mt-0.5">Site: {d.site}</p>}
        </div>

        {(d.body || d.rationaleContext || d.rationaleReasoning || d.rationaleAlternatives) && (
          <div>
            <SectionLabel>Why</SectionLabel>
            {d.body && <p className="text-sm text-[var(--text-sec)] leading-relaxed">{d.body}</p>}
            {d.rationaleContext && (
              <div className="mt-2">
                <span className="text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">Context</span>
                <p className="text-sm text-[var(--text-sec)] leading-relaxed">{d.rationaleContext}</p>
              </div>
            )}
            {d.rationaleReasoning && (
              <div className="mt-2">
                <span className="text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">Reasoning</span>
                <p className="text-sm text-[var(--text-sec)] leading-relaxed">{d.rationaleReasoning}</p>
              </div>
            )}
            {d.rationaleAlternatives && (
              <div className="mt-2">
                <span className="text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">Alternatives considered</span>
                <p className="text-sm text-[var(--text-sec)] leading-relaxed">{d.rationaleAlternatives}</p>
              </div>
            )}
          </div>
        )}

        {refs.length > 0 && (
          <div>
            <SectionLabel>Related objects</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {refs.map((r) => (
                <Link
                  key={`${r.kind}-${r.id}`}
                  to={r.href}
                  className="inline-flex items-center px-2 py-1 bg-[var(--surface)] hover:bg-[var(--hover-tint)] text-sm text-[var(--text-sec)] transition-colors"
                >
                  <span className="text-[var(--text-ter)] mr-1 capitalize">{r.kind}:</span>
                  {r.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {(d.supersedesDecisionId || supersededByIds.length > 0 || d.parentDecisionId || childrenIds.length > 0) && (
          <div>
            <SectionLabel>Standing &amp; links</SectionLabel>
            <div className="space-y-1.5">
              {d.supersedesDecisionId && linkButton(d.supersedesDecisionId, 'Supersedes', '#DC2626')}
              {supersededByIds.map((id) => linkButton(id, 'Superseded by', '#DC2626'))}
              {d.parentDecisionId && linkButton(d.parentDecisionId, 'Sub-decision of', '#6B7280')}
              {childrenIds.map((id) => linkButton(id, 'Sub-decision', '#6B7280'))}
            </div>
          </div>
        )}

        {(d.reviewByDate || d.reconsiderationConditions) && (
          <div>
            <SectionLabel>Revisit</SectionLabel>
            {d.reviewByDate && (
              <p className={`text-sm ${reviewDue ? 'text-[var(--mustard)] font-medium' : 'text-[var(--text-sec)]'}`}>
                Review by {formatDate(d.reviewByDate)}{reviewDue ? ' — overdue' : ''}
              </p>
            )}
            {d.reconsiderationConditions && (
              <p className="text-sm text-[var(--text-ter)] mt-1 leading-relaxed">
                <span className="font-medium text-[var(--text-sec)]">Reconsideration conditions:</span> {d.reconsiderationConditions}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DecisionMapView;
